import React, { useEffect, useMemo, useState } from "react";
import API from "../services/api";
import { getVendors, getCustomers, getCarriers, getCountries } from "../services/lookupCache";

const SHIPMENT_TYPE_OPTIONS = [
  { label: "Document", value: "document" },
  { label: "Sample", value: "sample" },
  { label: "Commercial", value: "commercial" }
];

const IMPORT_EXPORT_OPTIONS = [
  { label: "Export", value: "Export" },
  { label: "Import", value: "Import" },
  { label: "Domestic", value: "Domestic" }
];

const normalize = value => String(value || "").trim().toLowerCase();

const getTodayIsoDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const createInitialFormData = () => ({
  shipment_date: getTodayIsoDate(),
  customer_id: "",
  vendor_id: "",
  carrier_id: "",
  awb_number: "",
  booking_weight: "",
  shipment_type: "sample",
  import_export: "Export",
  source_country_id: "",
  destination_country_id: "",
  source_state: "",
  destination_state: "",
  pickup_cost: "",
  notes: ""
});

const createInitialFieldInputs = () => ({
  customer_id: "",
  vendor_id: "",
  carrier_id: "",
  source_country_id: "",
  destination_country_id: "",
  shipment_type: "Sample",
  import_export: "Export"
});

const RECONCILIATION_STATUS_LABELS = {
  missing_from_system: "In WhatsApp, not in system",
  no_proof_found: "In system, no proof in WhatsApp",
  partial_match: "Possible match - please verify",
  no_document: "Mentioned in chat, no document",
  matched: "Matched"
};

const formatReconciliationRange = result =>
  result.reconciliation_date_start === result.reconciliation_date_end
    ? result.reconciliation_date_start
    : `${result.reconciliation_date_start} to ${result.reconciliation_date_end}`;

const RECONCILIATION_STATUS_BADGE_CLASS = {
  missing_from_system: "badge bg-danger",
  no_proof_found: "badge bg-warning text-dark",
  partial_match: "badge bg-info text-dark",
  no_document: "badge bg-secondary",
  matched: "badge bg-success"
};

function ShipmentEntry() {
  const [vendors, setVendors] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [todayShipments, setTodayShipments] = useState([]);
  const [bookingOrders, setBookingOrders] = useState({});
  const [formData, setFormData] = useState(createInitialFormData);
  const [fieldInputs, setFieldInputs] = useState(createInitialFieldInputs);
  const isDomestic = normalize(formData.import_export) === "domestic";

  const [reconciliationDateStart, setReconciliationDateStart] = useState(getTodayIsoDate);
  const [reconciliationDateEnd, setReconciliationDateEnd] = useState(getTodayIsoDate);
  const [reconciliationFile, setReconciliationFile] = useState(null);
  const [reconciliationResult, setReconciliationResult] = useState(null);
  const [reconciliationBusy, setReconciliationBusy] = useState(false);
  const [reconciliationError, setReconciliationError] = useState("");

  const indiaCountryId = useMemo(() => {
    const india = countries.find(
      country => country.country_name?.trim().toLowerCase() === "india"
    );
    return india ? String(india.id) : "";
  }, [countries]);

  // Load dropdowns and start auto-refresh
  useEffect(() => {
    getVendors().then(res => setVendors(res.data));
    getCustomers().then(res => setCustomers(res.data));
    getCarriers().then(res => setCarriers(res.data));
    getCountries().then(res => setCountries(res.data));
    API.get("/states", { params: { country_name: "India" } }).then(res => setStates(res.data));

    loadTodayShipments();
    let previousDate = getTodayIsoDate();

    // Auto refresh today's shipments and reset view when date changes.
    const interval = setInterval(() => {
      const currentDate = getTodayIsoDate();
      if (currentDate !== previousDate) {
        previousDate = currentDate;
        setFormData(current => ({
          ...createInitialFormData(),
          shipment_type: current.shipment_type || "sample",
          import_export: current.import_export || "Export"
        }));
        setFieldInputs(createInitialFieldInputs());
      }
      loadTodayShipments();
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!indiaCountryId) {
      return;
    }

    setFormData(current => {
      if (current.import_export === "Domestic") {
        if (
          current.source_country_id === indiaCountryId &&
          current.destination_country_id === indiaCountryId
        ) {
          return current;
        }

        return {
          ...current,
          source_country_id: indiaCountryId,
          destination_country_id: indiaCountryId
        };
      }

      if (current.import_export === "Import") {
        if (current.destination_country_id === indiaCountryId) {
          return current;
        }

        return {
          ...current,
          destination_country_id: indiaCountryId
        };
      }

      if (current.import_export === "Export") {
        if (current.source_country_id === indiaCountryId) {
          return current;
        }

        return {
          ...current,
          source_country_id: indiaCountryId
        };
      }

      return current;
    });
  }, [indiaCountryId, formData.import_export]);

  // Load only today's shipments.
  const loadTodayShipments = async () => {
    try {
      const res = await API.get("/shipments/today");
      setTodayShipments(res.data);
      // Which of them came from the Booking Queue, to link to their documents
      const awbs = (res.data || []).map(s => s.awb_number).filter(Boolean);
      if (awbs.length) {
        const params = new URLSearchParams();
        awbs.forEach(a => params.append("awb", a));
        API.get(`/booking-orders/for-shipments?${params.toString()}`)
          .then(r => setBookingOrders(r.data.items || {}))
          .catch(() => setBookingOrders({}));
      }
    } catch (error) {
      console.error("Error loading today's shipments", error);
    }
  };

  const loadLatestReconciliation = async (startIsoDate, endIsoDate) => {
    try {
      const res = await API.get("/reconciliation/awb/latest", {
        params: { reconciliation_date_start: startIsoDate, reconciliation_date_end: endIsoDate }
      });
      setReconciliationResult(res.data.run_id ? res.data : null);
    } catch (error) {
      console.error("Error loading reconciliation result", error);
    }
  };

  useEffect(() => {
    if (reconciliationDateEnd < reconciliationDateStart) {
      return;
    }
    loadLatestReconciliation(reconciliationDateStart, reconciliationDateEnd);
  }, [reconciliationDateStart, reconciliationDateEnd]);

  const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

  const pollReconciliationRun = async runId => {
    // Processing runs in a background task and can take a few minutes for a
    // busy day (each attachment is read one at a time) - poll rather than
    // hold a single long-lived request open.
    for (let attempt = 0; attempt < 90; attempt += 1) {
      await sleep(4000);
      try {
        const res = await API.get(`/reconciliation/awb/status/${runId}`);
        setReconciliationResult(res.data);
        if (res.data.status !== "processing") {
          return;
        }
      } catch (error) {
        console.error("Error polling reconciliation status", error);
        setReconciliationError("Lost track of this check - reload the page to see its status.");
        return;
      }
    }
    setReconciliationError("This is taking longer than expected - reload the page in a bit to see the result.");
  };

  const handleReconciliationUpload = async () => {
    if (!reconciliationFile) {
      alert("Choose the WhatsApp export ZIP file first");
      return;
    }
    if (reconciliationDateEnd < reconciliationDateStart) {
      alert("End date can't be before the start date");
      return;
    }

    setReconciliationBusy(true);
    setReconciliationError("");
    try {
      const body = new FormData();
      body.append("reconciliation_date_start", reconciliationDateStart);
      body.append("reconciliation_date_end", reconciliationDateEnd);
      body.append("file", reconciliationFile);

      const res = await API.post("/reconciliation/awb/upload", body, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setReconciliationResult(res.data);
      setReconciliationFile(null);
      await pollReconciliationRun(res.data.run_id);
    } catch (error) {
      console.error(error);
      setReconciliationError(error.response?.data?.detail || "Failed to check this export");
    } finally {
      setReconciliationBusy(false);
    }
  };

  // Handle input change
  const handleChange = e => {
    const { name, value } = e.target;

    setFormData(current => ({
      ...current,
      [name]: value
    }));
  };

  const resolveRecordByLabel = (options, labelKey, labelValue) =>
    options.find(item => normalize(item[labelKey]) === normalize(labelValue));

  const handleEntityInputChange = (name, value, options, labelKey) => {
    const matched = resolveRecordByLabel(options, labelKey, value);

    setFieldInputs(current => ({
      ...current,
      [name]: value
    }));

    setFormData(current => ({
      ...current,
      [name]: matched ? String(matched.id) : ""
    }));
  };

  const handleEnumInputChange = (name, value, options) => {
    const matched = options.find(option => normalize(option.label) === normalize(value));

    setFieldInputs(current => ({
      ...current,
      [name]: value
    }));

    setFormData(current => ({
      ...current,
      [name]: matched ? matched.value : ""
    }));
  };

  const getEntityLabelById = (options, labelKey, idValue) => {
    const matched = options.find(item => String(item.id) === String(idValue || ""));
    return matched ? matched[labelKey] : "";
  };

  useEffect(() => {
    if (!formData.source_country_id && !formData.destination_country_id) {
      return;
    }

    setFieldInputs(current => ({
      ...current,
      source_country_id: formData.source_country_id
        ? getEntityLabelById(countries, "country_name", formData.source_country_id)
        : current.source_country_id,
      destination_country_id: formData.destination_country_id
        ? getEntityLabelById(countries, "country_name", formData.destination_country_id)
        : current.destination_country_id
    }));
  }, [countries, formData.source_country_id, formData.destination_country_id]);

  // Save shipment
  const handleSubmit = async () => {
    const sourceState = formData.source_state?.trim() || "";
    const destinationState = formData.destination_state?.trim() || "";
    const missingCountryForNonDomestic =
      !isDomestic && (!formData.source_country_id || !formData.destination_country_id);
    const missingStateForDomestic = isDomestic && (!sourceState || !destinationState);

    if (
      !formData.shipment_date ||
      !formData.customer_id ||
      !formData.vendor_id ||
      !formData.carrier_id ||
      !formData.awb_number ||
      !formData.booking_weight ||
      !formData.shipment_type ||
      !formData.import_export ||
      missingCountryForNonDomestic ||
      missingStateForDomestic
    ) {
      alert(
        isDomestic
          ? "For domestic shipments, source state and destination state are mandatory."
          : "Please select valid values for all mandatory fields before saving."
      );
      return;
    }

    try {
      const payload = {
        shipment_date: formData.shipment_date,
        customer_id: parseInt(formData.customer_id),
        vendor_id: parseInt(formData.vendor_id),
        carrier_id: parseInt(formData.carrier_id),
        awb_number: formData.awb_number,
        booking_weight: parseFloat(formData.booking_weight),
        shipment_type: formData.shipment_type,
        import_export: formData.import_export,
        source_country_id: formData.source_country_id ? parseInt(formData.source_country_id) : null,
        destination_country_id: formData.destination_country_id ? parseInt(formData.destination_country_id) : null,
        source_state: sourceState || null,
        destination_state: destinationState || null,
        pickup_cost:
          formData.pickup_cost === "" || formData.pickup_cost === null
            ? null
            : parseFloat(formData.pickup_cost),
        notes: formData.notes?.trim() || null
      };

      await API.post("/shipments", payload);

      alert("Shipment saved successfully");
      setFormData(createInitialFormData());
      setFieldInputs(createInitialFieldInputs());
      loadTodayShipments();
    } catch (error) {
      console.error(error);
      alert("Error saving shipment");
    }
  };

  return (
    <div className="container mt-4">
      <h3>Shipment Entry</h3>

      <div className="row">
        <div className="col-md-3">
          <label>Booking Date</label>
          <input
            type="date"
            name="shipment_date"
            className="form-control"
            value={formData.shipment_date}
            onChange={handleChange}
          />
        </div>

        <div className="col-md-3">
          <label>Client Name</label>
          <input
            name="customer_id_input"
            className="form-control"
            placeholder="Type or select client"
            list="customer-options"
            value={fieldInputs.customer_id}
            onChange={e =>
              handleEntityInputChange("customer_id", e.target.value, customers, "customer_name")
            }
          />
          <datalist id="customer-options">
            {customers.map(c => (
              <option key={c.id} value={c.customer_name} />
            ))}
          </datalist>
        </div>

        <div className="col-md-3">
          <label>Vendor</label>
          <input
            name="vendor_id_input"
            className="form-control"
            placeholder="Type or select vendor"
            list="vendor-options"
            value={fieldInputs.vendor_id}
            onChange={e =>
              handleEntityInputChange("vendor_id", e.target.value, vendors, "vendor_name")
            }
          />
          <datalist id="vendor-options">
            {vendors.map(v => (
              <option key={v.id} value={v.vendor_name} />
            ))}
          </datalist>
        </div>

        <div className="col-md-3">
          <label>Carrier</label>
          <input
            name="carrier_id_input"
            className="form-control"
            placeholder="Type or select carrier"
            list="carrier-options"
            value={fieldInputs.carrier_id}
            onChange={e =>
              handleEntityInputChange("carrier_id", e.target.value, carriers, "carrier_name")
            }
          />
          <datalist id="carrier-options">
            {carriers.map(c => (
              <option key={c.id} value={c.carrier_name} />
            ))}
          </datalist>
        </div>

        <div className="col-md-3 mt-3">
          <label>AWB Number</label>
          <input
            type="text"
            name="awb_number"
            className="form-control"
            value={formData.awb_number}
            onChange={handleChange}
          />
        </div>

        <div className="col-md-3 mt-3">
          <label>Booking Weight</label>
          <input
            type="number"
            name="booking_weight"
            className="form-control"
            value={formData.booking_weight}
            onChange={handleChange}
          />
        </div>

        <div className="col-md-3 mt-3">
          <label>Shipment Type</label>
          <input
            name="shipment_type_input"
            className="form-control"
            list="shipment-type-options"
            value={fieldInputs.shipment_type}
            onChange={e => handleEnumInputChange("shipment_type", e.target.value, SHIPMENT_TYPE_OPTIONS)}
          />
          <datalist id="shipment-type-options">
            {SHIPMENT_TYPE_OPTIONS.map(option => (
              <option key={option.value} value={option.label} />
            ))}
          </datalist>
        </div>

        <div className="col-md-3 mt-3">
          <label>Import / Export</label>
          <input
            name="import_export_input"
            className="form-control"
            list="import-export-options"
            value={fieldInputs.import_export}
            onChange={e => handleEnumInputChange("import_export", e.target.value, IMPORT_EXPORT_OPTIONS)}
          />
          <datalist id="import-export-options">
            {IMPORT_EXPORT_OPTIONS.map(option => (
              <option key={option.value} value={option.label} />
            ))}
          </datalist>
        </div>

        <div className="col-md-3 mt-3">
          <label>Source Country</label>
          <input
            name="source_country_id_input"
            className="form-control"
            placeholder="Type or select source country"
            list="source-country-options"
            value={fieldInputs.source_country_id}
            onChange={e =>
              handleEntityInputChange("source_country_id", e.target.value, countries, "country_name")
            }
            disabled={isDomestic}
          />
          <datalist id="source-country-options">
            {countries.map(c => (
              <option key={c.id} value={c.country_name} />
            ))}
          </datalist>
        </div>

        <div className="col-md-3 mt-3">
          <label>Destination Country</label>
          <input
            name="destination_country_id_input"
            className="form-control"
            placeholder="Type or select destination country"
            list="destination-country-options"
            value={fieldInputs.destination_country_id}
            onChange={e =>
              handleEntityInputChange("destination_country_id", e.target.value, countries, "country_name")
            }
            disabled={isDomestic}
          />
          <datalist id="destination-country-options">
            {countries.map(c => (
              <option key={c.id} value={c.country_name} />
            ))}
          </datalist>
        </div>

        <div className="col-md-3 mt-3">
          <label>Source State</label>
          <input
            type="text"
            name="source_state"
            className="form-control"
            list="source-state-options"
            value={formData.source_state}
            onChange={handleChange}
            placeholder={isDomestic ? "Enter source state" : "Required only for domestic"}
            disabled={!isDomestic}
          />
          <datalist id="source-state-options">
            {states.map(s => (
              <option key={`src-${s.id}`} value={s.state_name} />
            ))}
          </datalist>
        </div>

        <div className="col-md-3 mt-3">
          <label>Destination State</label>
          <input
            type="text"
            name="destination_state"
            className="form-control"
            list="destination-state-options"
            value={formData.destination_state}
            onChange={handleChange}
            placeholder={isDomestic ? "Enter destination state" : "Required only for domestic"}
            disabled={!isDomestic}
          />
          <datalist id="destination-state-options">
            {states.map(s => (
              <option key={`dest-${s.id}`} value={s.state_name} />
            ))}
          </datalist>
        </div>

        <div className="col-md-3 mt-3">
          <label>Pickup Cost Incurred</label>
          <input
            type="number"
            name="pickup_cost"
            className="form-control"
            value={formData.pickup_cost}
            onChange={handleChange}
            min="0"
            step="0.01"
            placeholder="Optional"
          />
        </div>

        <div className="col-md-6 mt-3">
          <label>Notes / Remarks</label>
          <textarea
            name="notes"
            className="form-control"
            rows="2"
            value={formData.notes}
            onChange={handleChange}
            placeholder="Enter notes or remarks"
          />
        </div>
      </div>

      <button className="btn btn-primary mt-3" onClick={handleSubmit}>
        Save Shipment
      </button>

      <hr />

      <h4>Today's Shipments</h4>

      <table className="table table-bordered">
        <thead>
          <tr>
            <th>S.No</th>
            <th>AWB</th>
            <th>Date</th>
            <th>Customer</th>
            <th>Vendor</th>
            <th>Carrier</th>
            <th>Weight</th>
            <th>Pickup Cost</th>
            <th>Booking order</th>
          </tr>
        </thead>

        <tbody>
          {todayShipments.map((s, index) => (
            <tr key={index}>
              <td>{index + 1}</td>
              <td>{s.awb_number}</td>
              <td>{s.shipment_date}</td>
              <td>{s.customer}</td>
              <td>{s.vendor}</td>
              <td>{s.carrier}</td>
              <td>{s.weight}</td>
              <td>{s.pickup_cost ?? "-"}</td>
              <td>
                {bookingOrders[s.awb_number] ? (
                  <a href={`/booking-queue?order=${bookingOrders[s.awb_number].order_id}`}>{bookingOrders[s.awb_number].order_number}</a>
                ) : "-"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <hr />

      <h4>Reconcile Bookings (WhatsApp)</h4>
      <p className="text-muted">
        Upload the WhatsApp export ZIP for a range of days' "Shipments booked on a day" chat (forward those messages
        into a fresh chat first, then Export Chat &rarr; Include Media - exporting the main group directly can
        silently drop attachments). We'll check every AWB mentioned there against Shipment Entry for that same range.
      </p>

      <div className="row align-items-end">
        <div className="col-md-2">
          <label>From</label>
          <input
            type="date"
            className="form-control"
            value={reconciliationDateStart}
            onChange={e => {
              const value = e.target.value;
              setReconciliationDateStart(value);
              if (reconciliationDateEnd < value) {
                setReconciliationDateEnd(value);
              }
            }}
          />
        </div>
        <div className="col-md-2">
          <label>To</label>
          <input
            type="date"
            className="form-control"
            value={reconciliationDateEnd}
            min={reconciliationDateStart}
            onChange={e => setReconciliationDateEnd(e.target.value)}
          />
        </div>
        <div className="col-md-4">
          <label>WhatsApp export ZIP</label>
          <input
            type="file"
            accept=".zip"
            className="form-control"
            onChange={e => setReconciliationFile(e.target.files?.[0] || null)}
          />
        </div>
        <div className="col-md-3">
          <button className="btn btn-primary" onClick={handleReconciliationUpload} disabled={reconciliationBusy}>
            {reconciliationBusy ? "Checking..." : "Check Gaps"}
          </button>
        </div>
      </div>

      {reconciliationError && <div className="alert alert-danger mt-3">{reconciliationError}</div>}

      {reconciliationResult && reconciliationResult.status === "processing" && (
        <div className="alert alert-info mt-3">
          Checking the {formatReconciliationRange(reconciliationResult)} export... this can take a few minutes for a
          busy range. You can leave this page and come back later - the result will be waiting.
        </div>
      )}

      {reconciliationResult && reconciliationResult.status === "failed" && (
        <div className="alert alert-danger mt-3">
          Checking this export failed: {reconciliationResult.error_message || "unknown error"}
        </div>
      )}

      {reconciliationResult && reconciliationResult.status === "completed" && (
        <div className="mt-3">
          <p>
            Last checked {reconciliationResult.attachment_count} document(s) across{" "}
            {reconciliationResult.message_count} message(s) for {formatReconciliationRange(reconciliationResult)}
            {reconciliationResult.uploaded_by ? ` by ${reconciliationResult.uploaded_by}` : ""}.
            {reconciliationResult.counts && (
              <>
                {" "}Matched: {reconciliationResult.counts.matched}, In WhatsApp but not in system:{" "}
                {reconciliationResult.counts.missing_from_system}, In system with no proof:{" "}
                {reconciliationResult.counts.no_proof_found}, Possible matches to verify:{" "}
                {reconciliationResult.counts.partial_match || 0}, Mentioned with no document:{" "}
                {reconciliationResult.counts.no_document}.
              </>
            )}
          </p>
          <table className="table table-bordered">
            <thead>
              <tr>
                <th>Status</th>
                <th>AWB</th>
                <th>Source</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {reconciliationResult.items.map(item => (
                <tr key={item.id}>
                  <td>
                    <span className={RECONCILIATION_STATUS_BADGE_CLASS[item.status] || "badge bg-light text-dark"}>
                      {RECONCILIATION_STATUS_LABELS[item.status] || item.status}
                    </span>
                  </td>
                  <td>{item.awb_number || "-"}</td>
                  <td>{item.source_filename || item.source_label || "-"}</td>
                  <td>{item.notes || "-"}</td>
                </tr>
              ))}
              {reconciliationResult.items.length === 0 && (
                <tr>
                  <td colSpan="4">No gaps found for this day.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ShipmentEntry;
